import React, { useEffect, useState } from "react";
import { useNavigate, useOutletContext, useParams } from "react-router-dom";
import { adminFetch } from "../../api/adminApi";
import { AdminCard, AdminLabel, AdminPageHeader, SourceBadge } from "./AdminPageHeader";
import AdminRichEditor, { AdminHtmlPreviewFrame } from "./AdminRichEditor";

export default function AdminArticleCanonical() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { me } = useOutletContext() || {};
  const canEdit = me?.role === "admin";
  const [data, setData] = useState(null);
  const [headline, setHeadline] = useState("");
  const [bodyHtml, setBodyHtml] = useState("");
  const [summary, setSummary] = useState("");
  const [category, setCategory] = useState("");
  const [err, setErr] = useState("");
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const d = await adminFetch(`/v1/admin/articles/${id}`);
        if (cancelled) return;
        setData(d);
        const a = d.article;
        setHeadline(a.headline);
        setBodyHtml(a.body_html);
        setSummary(a.summary);
        setCategory(a.category || "");
      } catch (e) {
        if (!cancelled) setErr(e.message);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  const save = async (e) => {
    e.preventDefault();
    if (!canEdit) return;
    setErr("");
    setSaved(false);
    try {
      await adminFetch(`/v1/admin/articles/${id}`, {
        method: "PATCH",
        body: JSON.stringify({
          headline,
          body_html: bodyHtml,
          summary,
          category,
        }),
      });
      setSaved(true);
    } catch (e) {
      setErr(e.data?.error || e.message);
    }
  };

  if (!data && !err) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-[#CCFF00]/30 border-t-[#CCFF00]" />
          <p className="font-mono-data text-xs uppercase tracking-widest text-slate-500">
            Loading article…
          </p>
        </div>
      </div>
    );
  }
  if (err && !data) {
    return (
      <div className="rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-red-300">
        {err}
      </div>
    );
  }

  return (
    <div>
      <AdminPageHeader
        kicker="Canonical"
        title="Article editor"
        description={
          <>
            <span className="font-mono-data text-slate-500">
              {data.article.external_id}
            </span>
            <span className="mx-2 text-slate-600">·</span>
            <SourceBadge source={data.article.source} />
          </>
        }
      >
        <button
          type="button"
          className="pitch-admin-btn-ghost"
          onClick={() => navigate(-1)}
        >
          ← Back
        </button>
      </AdminPageHeader>

      {!canEdit && (
        <div className="mb-6 rounded-xl border border-amber-500/25 bg-amber-500/10 px-4 py-3 text-sm text-amber-200/90">
          You're viewing this article as a writer. Only admins can save changes to the master copy.
        </div>
      )}

      <AdminCard>
        <form onSubmit={save} className="space-y-6">
          <div>
            <AdminLabel>Headline</AdminLabel>
            <input
              className="pitch-admin-input"
              value={headline}
              onChange={(e) => setHeadline(e.target.value)}
              readOnly={!canEdit}
            />
          </div>
          <div>
            <AdminLabel>Summary</AdminLabel>
            <input
              className="pitch-admin-input"
              value={summary}
              onChange={(e) => setSummary(e.target.value)}
              readOnly={!canEdit}
            />
          </div>
          <div>
            <AdminLabel>Category</AdminLabel>
            <input
              className="pitch-admin-input"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              readOnly={!canEdit}
            />
          </div>
          <div>
            <AdminLabel>Body</AdminLabel>
            <AdminRichEditor
              value={bodyHtml}
              onChange={setBodyHtml}
              readOnly={!canEdit}
              placeholder="Edit the global story…"
              helperText="Changes here update the master copy and refresh translations for all clients."
              size="large"
              showPreview
            />
          </div>
          {err && (
            <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">
              {err}
            </div>
          )}
          {saved && (
            <p className="font-mono-data text-sm text-[#CCFF00]">
              Saved — downstream translations will refresh in the background.
            </p>
          )}
          {canEdit && (
            <button type="submit" className="pitch-admin-btn-primary">
              Save canonical
            </button>
          )}
        </form>
      </AdminCard>

      {(data.article.wire_headline || data.article.wire_body_html) && (
        <AdminCard className="mt-8">
          <div className="mb-4 flex items-center gap-3">
            <h3 className="text-[10px] font-black uppercase tracking-[0.25em] text-slate-500">
              Source wire copy
            </h3>
            <span className="rounded-full border border-amber-500/25 bg-amber-500/[0.08] px-2 py-0.5 font-mono-data text-[9px] uppercase tracking-widest text-amber-400/80">
              Goalise original
            </span>
          </div>
          <p className="mb-5 text-xs text-slate-500">
            The original article as received from the source, before any edits were made.
          </p>
          {data.article.wire_headline && (
            <div className="mb-4">
              <AdminLabel>Original headline</AdminLabel>
              <p className="mt-1 rounded-lg border border-white/[0.06] bg-black/30 px-4 py-3 text-sm text-slate-200">
                {data.article.wire_headline}
              </p>
            </div>
          )}
          {data.article.wire_summary && (
            <div className="mb-4">
              <AdminLabel>Original summary</AdminLabel>
              <p className="mt-1 rounded-lg border border-white/[0.06] bg-black/30 px-4 py-3 text-sm text-slate-400">
                {data.article.wire_summary}
              </p>
            </div>
          )}
          {data.article.wire_body_html && (
            <div>
              <AdminLabel>Original body</AdminLabel>
              <div className="mt-1">
                <AdminHtmlPreviewFrame html={data.article.wire_body_html} label="Wire copy" />
              </div>
            </div>
          )}
        </AdminCard>
      )}

      {data.customer_articles?.length > 0 && (
        <AdminCard className="mt-8">
          <h3 className="mb-4 text-[10px] font-black uppercase tracking-[0.25em] text-slate-500">
            Override coverage
          </h3>
          <ul className="space-y-3">
            {data.customer_articles.map((r) => (
              <li
                key={r.customer_id}
                className="flex items-center justify-between gap-4 rounded-lg border border-white/[0.06] bg-black/30 px-4 py-3"
              >
                <span className="text-sm font-medium text-slate-200">
                  {r.customer_name}
                </span>
                <span className="font-mono-data text-[10px] uppercase tracking-widest text-slate-500">
                  {r.headline_override || r.body_html_override || r.summary_override
                    ? "Override on"
                    : "Default delivery"}
                </span>
              </li>
            ))}
          </ul>
        </AdminCard>
      )}
    </div>
  );
}